// src/components/dashboard/masteradmin/MasterAdminDashboardView.js
// The master admin's cards+charts+company-drilldown, extracted so Super
// Admin's dashboard can embed it verbatim underneath its own inventory
// cards, without a route change or duplicated data-fetching. Mirrors the
// CompanyDashboardView/DepartmentDashboardView extraction pattern.
import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import StatTile from '../departmentadmin/StatTile';
import MessageBoardWidget from '../MessageBoardWidget';
import ParticipantTrendChart from '../departmentadmin/ParticipantTrendChart';
import TrainingHoursChart from '../departmentadmin/TrainingHoursChart';
import CompanyDashboardView from '../companyadmin/CompanyDashboardView';
import CompanyComparisonChart from './CompanyComparisonChart';
import TopCoursesList from './TopCoursesList';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MasterAdminDashboardView() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null); // { id, name }

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/stat/get_org_dashboard_stats.php`, {
          withCredentials: true
        });
        if (isMounted) setStats(response.data);
      } catch (error) {
        console.error('Error fetching org dashboard stats:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();
    return () => { isMounted = false; };
  }, []);

  if (selectedCompany) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{selectedCompany.name}</h5>
          <Button variant="outline-secondary" size="sm" onClick={() => setSelectedCompany(null)}>
            &larr; Back to Org Overview
          </Button>
        </div>
        <CompanyDashboardView companyIdOverride={selectedCompany.id} />
      </div>
    );
  }

  return (
    <>
      <Row className="g-3 mb-1">
        <Col md={3}>
          <StatTile title="Upcoming Trainings" icon="bi-calendar-event" value={stats?.upcomingTrainingsCount ?? 0} loading={loading} />
        </Col>
        <Col md={3}>
          <StatTile title="Employees Assigned" icon="bi-people" value={stats?.employeesAssignedCount ?? 0} loading={loading} />
        </Col>
        <Col md={3}>
          <StatTile title="Pending Requests" icon="bi-hourglass-split" value={stats?.pendingRequestsCount ?? 0} loading={loading} />
        </Col>
        <Col md={3}>
          <StatTile title="Certificates This Month" icon="bi-award" value={stats?.certificatesThisMonthCount ?? 0} loading={loading} />
        </Col>
        {/* No override prop here (unlike CompanyDashboardView/
            DepartmentDashboardView) — this component is always the
            viewer's own scope, whether reached directly as master admin or
            embedded in SuperAdminDashboard.js, so showing it unconditionally
            also covers Super Admin without a separate widget there. */}
        <Col md={3}>
          <MessageBoardWidget />
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col md={4}>
          <StatTile title="KI-Eligible Employees" icon="bi-mortarboard" value={stats?.kiEligibleCount ?? 0} loading={loading} />
        </Col>
        <Col md={8}>
          <TopCoursesList data={stats?.topCourses ?? []} loading={loading} />
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col md={6}>
          <ParticipantTrendChart data={stats?.participantTrend ?? []} loading={loading} />
        </Col>
        <Col md={6}>
          <TrainingHoursChart data={stats?.trainingHours ?? []} loading={loading} />
        </Col>
      </Row>
      <Row className="g-3 mt-1">
        <Col md={12}>
          <CompanyComparisonChart
            data={stats?.companyComparison ?? []}
            loading={loading}
            onSelectCompany={(company) => setSelectedCompany(company)}
          />
        </Col>
      </Row>
    </>
  );
}

export default MasterAdminDashboardView;
